import { actionCreator, ActionReturnType, hasValue, Loading, loadingFailed, loadingFinished, loadingIsLoading, loadingNotStarted } from "@itk/typescript-utils";
import { WarningAction } from "./warning-dialog/warning-dialog-types";
import { CurrentIdentitySettingService, Model_ContactPersonPut, Model_CustomerPortalPossibleCustomerGet, Shared_SystemSettings } from "src/backend/openapi/v1";
import { ContactPersonDetailsGet, CustomerGet, CustomerSettings, FileModelState } from "./application-types";
import { IDENTITY_SETTINGS_KEYS, TIdentitySettings } from "src/utils/types/identity-settings-types";

export enum Action {
  BlockCacheRenewal = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:BLOCKCACHERENEWAL",
  SetIsSidebarOpen = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETISSIDEBAROPEN",
  SetWarningAction = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETWARNINGACTION",
  SetGlobalSearch = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETGLOBALSEARCH",

  GetPossibleCustomersRequest = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETPOSSIBLECUSTOMERSREQUEST",
  GetPossibleCustomersSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETPOSSIBLECUSTOMERSSUCCESS",
  GetPossibleCustomersFail = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETPOSSIBLECUSTOMERSFAIL",

  GetCustomerSettingsSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCUSTOMERSETTINGSSUCCESS",

  GetContactPersonDetailsRequest = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCONTACTPERSONDETAILSREQUEST",
  GetContactPersonDetailsSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCONTACTPERSONDETAILSSUCCESS",
  GetContactPersonDetailsFail = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCONTACTPERSONDETAILSFAIL",

  UpdateContactPersonDetailRequest = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:UPDATECONTACTPERSONDETAILREQUEST",

  GetCustomerRequestById = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCUSTOMERREQUESTBYID",
  GetCustomerSuccessById = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETCUSTOMERSUCCESSBYID",

  GetSystemSettingsValueByIdRequest = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETSYSTEMSETTINGSVALUEBYIDREQUEST",
  GetSystemSettingsValueByIdSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETSYSTEMSETTINGSVALUEBYIDSUCCESS",
  GetSystemSettingsValueByIdFail = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETSYSTEMSETTINGSVALUEBYIDFAIL",

  GetIdentitySettingsSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:GETIDENTITYSETTINGSSUCCESS",
  SetIdentitySettingsRequest = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETIDENTITYSETTINGSREQUEST",
  SetIdentitySettingsSuccess = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETIDENTITYSETTINGSSUCCESS",

  ShowFile = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SHOWFILE",
  ResetFile = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:RESETFILE",

  SetGlobalLoading = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETGLOBALLOADING",
  SetGlobalLoadingBar = "@@ITK:SRC:COMPONENTS:APPLICATION-STORE.TS:ACTION:SETGLOBALLOADINGBAR"
}

export type ApplicationState = {
  blockCacheRenewal: boolean;
  isSidebarOpen: boolean;
  warningAction: WarningAction | undefined;
  contactPersonDetails: Loading<ContactPersonDetailsGet>;
  customers: Loading<Model_CustomerPortalPossibleCustomerGet[]>;
  currentCustomer: Loading<CustomerSettings>;
  customerDetails: Loading<CustomerGet>;
  openFileModel?: FileModelState;
  systemSettings: Partial<Record<Shared_SystemSettings, string>>;
  identitySettings: Partial<TIdentitySettings>;
  globalLoading: boolean;
  globalLoadingBar: boolean;
};

const initialState: ApplicationState = {
  blockCacheRenewal: false,
  isSidebarOpen: true,
  warningAction: undefined,
  contactPersonDetails: loadingNotStarted,
  customers: loadingNotStarted,
  currentCustomer: loadingNotStarted,
  customerDetails: loadingNotStarted,
  openFileModel: undefined,
  systemSettings: {},
  identitySettings: {},
  globalLoading: false,
  globalLoadingBar: false
};

export const applicationActions = {
  setBlockCacheRenewal: (blockCacheRenewal: boolean) => actionCreator(Action.BlockCacheRenewal, { blockCacheRenewal }),
  setIsSidebarOpen: (isOpen: ApplicationState["isSidebarOpen"]) => actionCreator(Action.SetIsSidebarOpen, { isOpen }),
  setWarningAction: (warningAction: WarningAction | undefined) => actionCreator(Action.SetWarningAction, { warningAction }),

  getContactPersonDetailsRequest: (customerId: string) => actionCreator(Action.GetContactPersonDetailsRequest, { customerId }),
  getContactPersonDetailsSuccess: (contactPersonDetails: ContactPersonDetailsGet) => actionCreator(Action.GetContactPersonDetailsSuccess, { contactPersonDetails }),
  getContactPersonDetailsFail: () => actionCreator(Action.GetContactPersonDetailsFail),

  updateContactPersonDetailsRequest: (data: Partial<Model_ContactPersonPut>) => actionCreator(Action.UpdateContactPersonDetailRequest, { data }),

  getPossibleCustomersRequest: () => actionCreator(Action.GetPossibleCustomersRequest),
  getPossibleCustomersSuccess: (possibleCustomers: Model_CustomerPortalPossibleCustomerGet[]) => actionCreator(Action.GetPossibleCustomersSuccess, { possibleCustomers }),
  getPossibleCustomersFail: () => actionCreator(Action.GetPossibleCustomersFail),

  getCustomerSettingsSuccess: (customerSettings: CustomerSettings) => actionCreator(Action.GetCustomerSettingsSuccess, { customerSettings }),

  getCustomerRequestById: (customerId: string) => actionCreator(Action.GetCustomerRequestById, { customerId }),
  getCustomerSuccessById: (data: CustomerGet) => actionCreator(Action.GetCustomerSuccessById, { data }),

  getSystemSettingsValueByIdRequest: (systemSettingsId: Shared_SystemSettings) => actionCreator(Action.GetSystemSettingsValueByIdRequest, { systemSettingsId }),
  getSystemSettingsValueByIdSuccess: (systemSettingId: Shared_SystemSettings, value: string) => actionCreator(Action.GetSystemSettingsValueByIdSuccess, { systemSettingId, value }),

  getIdentitySettingsSuccess: (data: Awaited<ReturnType<typeof CurrentIdentitySettingService.getApiV1CurrentIdentitySettingsByIdentityId>>) =>
    actionCreator(Action.GetIdentitySettingsSuccess, { data }),

  setIdentitySettingsRequest: (key: IDENTITY_SETTINGS_KEYS, value: string) => actionCreator(Action.SetIdentitySettingsRequest, { key, value }),

  showFile: (openFileModel: FileModelState) => actionCreator(Action.ShowFile, { openFileModel }),
  resetFile: () => actionCreator(Action.ResetFile),

  setGlobalLoadingBar: (loading: boolean) => actionCreator(Action.SetGlobalLoadingBar, { loading }),
  setGlobalLoading: (loading: boolean) => actionCreator(Action.SetGlobalLoading, { loading })
};

export const reducer = (state = initialState, action: ActionReturnType<typeof applicationActions>): ApplicationState => {
  switch (action.type) {
    case Action.BlockCacheRenewal:
      return { ...state, blockCacheRenewal: action.payload.blockCacheRenewal };
    case Action.SetIsSidebarOpen:
      return { ...state, isSidebarOpen: action.payload.isOpen };
    case Action.SetWarningAction:
      return { ...state, warningAction: action.payload.warningAction };

    case Action.GetContactPersonDetailsRequest:
      return { ...state, contactPersonDetails: loadingIsLoading };
    case Action.GetContactPersonDetailsSuccess:
      return { ...state, contactPersonDetails: loadingFinished(action.payload.contactPersonDetails) };
    case Action.GetContactPersonDetailsFail:
      return { ...state, contactPersonDetails: loadingFailed };

    case Action.GetPossibleCustomersRequest:
      return { ...state, customers: loadingIsLoading };
    case Action.GetPossibleCustomersSuccess:
      return { ...state, customers: loadingFinished(action.payload.possibleCustomers) };
    case Action.GetPossibleCustomersFail:
      return { ...state, customers: loadingFailed };

    case Action.GetCustomerSettingsSuccess:
      return { ...state, currentCustomer: loadingFinished(action.payload.customerSettings) };

    case Action.GetCustomerRequestById:
      return { ...state, customerDetails: loadingIsLoading };
    case Action.GetCustomerSuccessById:
      return { ...state, customerDetails: loadingFinished(action.payload.data) };

    case Action.GetSystemSettingsValueByIdSuccess: {
      const oldState = state.systemSettings ? state.systemSettings : {};
      const payload = {
        ...oldState,
        [action.payload.systemSettingId]: action.payload.value
      };
      return { ...state, systemSettings: payload };
    }

    case Action.GetIdentitySettingsSuccess: {
      const newData = {} as typeof state.identitySettings;
      Object.keys(action.payload.data).forEach(key => {
        const value = action.payload.data[key] || "{}";
        newData[key as IDENTITY_SETTINGS_KEYS] = value.isJsonObject() ? JSON.parse(value) : value;
      });
      return { ...state, identitySettings: { ...state.identitySettings, ...newData } };
    }

    case Action.ShowFile:
      return {
        ...state,
        openFileModel: { ...action.payload.openFileModel, previewAvailable: hasValue(!action.payload.openFileModel.previewAvailable) ? true : action.payload.openFileModel.previewAvailable }
      };
    case Action.ResetFile: {
      return { ...state, openFileModel: initialState.openFileModel };
    }

    case Action.SetGlobalLoadingBar:
      return { ...state, globalLoadingBar: action.payload.loading };
    case Action.SetGlobalLoading:
      return { ...state, globalLoading: action.payload.loading };

    default:
      return state;
  }
};
